import { type MeDto, PASSWORD_MIN_LENGTH } from "@clinic/shared";
import { type ReactNode, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router";
import { ApiError, api } from "../api";
import { fieldErrors, formValues, hasFieldErrors } from "../forms";
import { LanguageSwitcher, useT } from "../i18n";
import { usePageMeta } from "../seo";
import { useMe, useSessionMutation } from "../session";
import { AcceptTerms, SiteFooter, SiteHeader, withAcceptTerms } from "../site";
import { Alert, Button, Card, Field, FullPageSpinner, Logo } from "../ui";

export function AuthShell({ title, subtitle, children }: { title: string; subtitle?: ReactNode; children: ReactNode }) {
  return (
    <main className="flex min-h-screen flex-col items-center justify-center px-4 py-12">
      <div className="mb-8 flex w-full max-w-md items-center justify-between">
        <Link to="/">
          <Logo />
        </Link>
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-md">
        <h1 className="text-xl font-semibold">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-slate-600">{subtitle}</p> : null}
        <div className="mt-6">{children}</div>
      </Card>
    </main>
  );
}

/** Pages for signed-out visitors bounce signed-in users to the app. */
function SignedOutOnly({ children }: { children: ReactNode }) {
  const me = useMe();
  if (me.isPending) return <FullPageSpinner />;
  if (me.data) return <Navigate to="/app" replace />;
  return <>{children}</>;
}

export function LandingPage() {
  const { t } = useT();
  usePageMeta("landing");
  const features = [1, 2, 3, 4, 5, 6] as const;
  const steps = [1, 2, 3] as const;
  const faqs = [1, 2, 3, 4, 5] as const;
  const primary =
    "rounded-lg bg-brand-600 px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-brand-900/30 hover:bg-brand-500";
  return (
    <div className="min-h-screen bg-white">
      <div className="bg-deep-sea text-white">
        <SiteHeader tone="light" />
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-6 pt-8 pb-20 lg:grid-cols-[1.1fr_0.9fr] lg:pt-16 lg:pb-28">
          <div>
            <p className="text-sm font-semibold tracking-wide text-aqua-300 uppercase">{t("landing.tagline")}</p>
            <h1 className="mt-3 text-4xl font-bold tracking-tight text-balance sm:text-5xl">{t("landing.headline")}</h1>
            <p className="mt-5 max-w-xl text-lg text-aqua-50/85">{t("landing.lede")}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/signup" className={primary}>
                {t("landing.cta_setup")}
              </Link>
              <Link
                to="/login"
                className="rounded-lg px-5 py-3 text-sm font-semibold text-white ring-1 ring-white/40 hover:bg-white/10"
              >
                {t("landing.cta_account")}
              </Link>
            </div>
            <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-sm text-aqua-100">
              {(["trust_eu", "trust_gdpr", "trust_lang"] as const).map((k) => (
                <li key={k} className="flex items-center gap-2">
                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-aqua-400" />
                  {t(`landing.${k}`)}
                </li>
              ))}
            </ul>
          </div>
          <img
            src="/dolphin.webp"
            alt={t("landing.hero_alt")}
            width={640}
            height={640}
            fetchPriority="high"
            className="mx-auto w-64 drop-shadow-[0_0_60px_rgba(8,197,251,0.45)] sm:w-80 lg:w-full lg:max-w-md"
          />
        </section>
      </div>

      <main>
        <section className="mx-auto max-w-6xl px-6 py-20" aria-labelledby="features">
          <h2 id="features" className="max-w-2xl text-3xl font-bold tracking-tight text-balance">
            {t("landing.features_title")}
          </h2>
          <ul className="mt-10 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((n) => (
              <li key={n} className="rounded-2xl bg-slate-50 p-6 ring-1 ring-slate-200">
                <span
                  aria-hidden="true"
                  className="block h-1 w-10 rounded-full bg-gradient-to-r from-brand-600 to-aqua-500"
                />
                <h3 className="mt-4 font-semibold">{t(`landing.feature${n}_title`)}</h3>
                <p className="mt-2 text-sm text-slate-600">{t(`landing.feature${n}_text`)}</p>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-brand-50" aria-labelledby="steps">
          <div className="mx-auto max-w-6xl px-6 py-20">
            <h2 id="steps" className="text-3xl font-bold tracking-tight">
              {t("landing.steps_title")}
            </h2>
            <ol className="mt-10 grid gap-8 sm:grid-cols-3">
              {steps.map((n) => (
                <li key={n}>
                  <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-700 font-bold text-white">
                    {n}
                  </span>
                  <h3 className="mt-4 font-semibold">{t(`landing.step${n}_title`)}</h3>
                  <p className="mt-2 text-sm text-slate-600">{t(`landing.step${n}_text`)}</p>
                </li>
              ))}
            </ol>
            <p className="mt-12 text-sm font-semibold text-brand-900">{t("landing.audience_title")}</p>
            <p className="mt-1 text-sm text-slate-600">{t("landing.audience_list")}</p>
          </div>
        </section>

        <section className="mx-auto grid max-w-6xl gap-10 px-6 py-20 lg:grid-cols-2" aria-labelledby="privacy">
          <div>
            <h2 id="privacy" className="text-3xl font-bold tracking-tight">
              {t("landing.privacy_title")}
            </h2>
            <p className="mt-4 text-slate-600">{t("landing.privacy_text")}</p>
            <Link to="/privacy" className="mt-4 inline-block text-sm font-semibold text-brand-700 hover:underline">
              {t("landing.privacy_link")} →
            </Link>
          </div>
          <div>
            <h2 className="text-3xl font-bold tracking-tight">{t("landing.faq_title")}</h2>
            <div className="mt-4 divide-y divide-slate-200">
              {faqs.map((n) => (
                <details key={n} className="group py-4">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-4 font-semibold">
                    <h3>{t(`landing.faq${n}_q`)}</h3>
                    <span
                      aria-hidden="true"
                      className="text-brand-700 transition-transform group-open:rotate-45 motion-reduce:transition-none"
                    >
                      +
                    </span>
                  </summary>
                  <p className="mt-2 text-sm text-slate-600">{t(`landing.faq${n}_a`)}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-deep-sea text-white">
          <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-6 px-6 py-14">
            <div>
              <h2 className="text-2xl font-bold">{t("landing.cta_title")}</h2>
              <p className="mt-1 text-aqua-100">{t("landing.cta_text")}</p>
            </div>
            <Link to="/signup" className={primary}>
              {t("landing.cta_setup")}
            </Link>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}

export const slugify = (s: string) =>
  s
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/æ/g, "ae")
    .replace(/ø/g, "oe")
    .replace(/å/g, "aa")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

export function SignupPage() {
  const navigate = useNavigate();
  const { t, tError } = useT();
  usePageMeta("signup");
  const [clinicName, setClinicName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugEdited, setSlugEdited] = useState(false);
  const signup = useSessionMutation((input: Record<string, unknown>) => api<MeDto>("POST", "/auth/signup", input));
  const fields = fieldErrors(signup.error);
  const code = signup.error instanceof ApiError ? signup.error.code : undefined;

  return (
    <SignedOutOnly>
      <AuthShell
        title={t("auth.signup_title")}
        subtitle={
          <>
            {t("auth.already_registered")}{" "}
            <Link to="/login" className="font-medium text-brand-700 hover:underline">
              {t("auth.sign_in")}
            </Link>
          </>
        }
      >
        <form
          className="space-y-4"
          noValidate
          onSubmit={(e) => signup.mutate(withAcceptTerms(formValues(e)), { onSuccess: () => navigate("/app") })}
        >
          {signup.error && !hasFieldErrors(signup.error) && code !== "SLUG_TAKEN" && code !== "EMAIL_TAKEN" ? (
            <Alert>{tError(signup.error)}</Alert>
          ) : null}
          <Field
            label={t("auth.clinic_name")}
            name="clinicName"
            required
            value={clinicName}
            onChange={(e) => {
              setClinicName(e.target.value);
              if (!slugEdited) setSlug(slugify(e.target.value));
            }}
            error={fields.clinicName}
          />
          <Field
            label={t("auth.booking_address")}
            name="clinicSlug"
            required
            value={slug}
            onChange={(e) => {
              setSlugEdited(true);
              setSlug(e.target.value);
            }}
            hint={slug ? t("auth.booking_address_preview", { url: `/book/${slug}` }) : t("auth.booking_address_hint")}
            error={fields.clinicSlug ?? (code === "SLUG_TAKEN" ? tError(signup.error) : undefined)}
          />
          <Field label={t("auth.your_name")} name="fullName" autoComplete="name" required error={fields.fullName} />
          <Field
            label={t("auth.work_email")}
            name="email"
            type="email"
            autoComplete="email"
            required
            error={fields.email ?? (code === "EMAIL_TAKEN" ? tError(signup.error) : undefined)}
          />
          <Field
            label={t("auth.password")}
            name="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN_LENGTH}
            hint={t("auth.password_hint", { n: PASSWORD_MIN_LENGTH })}
            error={fields.password}
          />
          <AcceptTerms error={fields.acceptTerms} />
          <Button className="w-full" busy={signup.isPending}>
            {t("auth.create_clinic")}
          </Button>
        </form>
      </AuthShell>
    </SignedOutOnly>
  );
}

export function LoginPage() {
  const navigate = useNavigate();
  const { t, tError } = useT();
  usePageMeta("login");
  const login = useSessionMutation((input: Record<string, string>) => api<MeDto>("POST", "/auth/login", input));
  const message =
    login.error instanceof ApiError && login.error.status === 401
      ? t("auth.login_failed")
      : login.error
        ? tError(login.error)
        : null;
  return (
    <SignedOutOnly>
      <AuthShell
        title={t("auth.sign_in")}
        subtitle={
          <>
            {t("auth.new_here")}{" "}
            <Link to="/signup" className="font-medium text-brand-700 hover:underline">
              {t("auth.signup_title")}
            </Link>
          </>
        }
      >
        <form
          className="space-y-4"
          onSubmit={(e) => login.mutate(formValues(e), { onSuccess: () => navigate("/app") })}
        >
          {message ? <Alert>{message}</Alert> : null}
          <Field label={t("auth.email")} name="email" type="email" autoComplete="email" required />
          <Field label={t("auth.password")} name="password" type="password" autoComplete="current-password" required />
          <div className="flex justify-end">
            <Link to="/forgot-password" className="text-sm font-medium text-brand-700 hover:underline">
              {t("auth.forgot_password")}
            </Link>
          </div>
          <Button className="w-full" busy={login.isPending}>
            {t("auth.sign_in")}
          </Button>
        </form>
      </AuthShell>
    </SignedOutOnly>
  );
}

export function ForgotPasswordPage() {
  const { t, tError, locale } = useT();
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <AuthShell title={t("auth.reset_title")} subtitle={t("auth.reset_subtitle")}>
      {sent ? (
        <Alert tone="success">{t("auth.reset_sent")}</Alert>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            const values = formValues(e);
            setBusy(true);
            setError(null);
            try {
              await api("POST", "/auth/password-reset/request", { ...values, locale });
              setSent(true);
            } catch (err) {
              setError(tError(err));
            } finally {
              setBusy(false);
            }
          }}
        >
          {error ? <Alert>{error}</Alert> : null}
          <Field label={t("auth.email")} name="email" type="email" autoComplete="email" required />
          <Button className="w-full" busy={busy}>
            {t("auth.send_reset_link")}
          </Button>
        </form>
      )}
      <p className="mt-6 text-center text-sm">
        <Link to="/login" className="font-medium text-brand-700 hover:underline">
          {t("auth.back_to_sign_in")}
        </Link>
      </p>
    </AuthShell>
  );
}

export function ResetPasswordPage() {
  const { t, tError } = useT();
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";
  const [done, setDone] = useState(false);
  const [error, setError] = useState<{ message: string; fields: Record<string, string> } | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <AuthShell title={t("auth.new_password_title")}>
      {!token ? (
        <Alert>{t("auth.link_incomplete")}</Alert>
      ) : done ? (
        <div className="space-y-4">
          <Alert tone="success">{t("auth.password_changed_signed_out")}</Alert>
          <Link to="/login" className="block text-center text-sm font-medium text-brand-700 hover:underline">
            {t("auth.sign_in")}
          </Link>
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={async (e) => {
            const values = formValues(e);
            if (values.password !== values.confirm) {
              setError({ message: "", fields: { confirm: t("auth.passwords_mismatch") } });
              return;
            }
            setBusy(true);
            setError(null);
            try {
              await api("POST", "/auth/password-reset/confirm", { token, password: values.password });
              setDone(true);
            } catch (err) {
              setError({ message: tError(err), fields: fieldErrors(err) });
            } finally {
              setBusy(false);
            }
          }}
        >
          {error?.message && !error.fields.password ? <Alert>{error.message}</Alert> : null}
          <Field
            label={t("auth.new_password")}
            name="password"
            type="password"
            autoComplete="new-password"
            required
            minLength={PASSWORD_MIN_LENGTH}
            hint={t("auth.new_password_hint", { n: PASSWORD_MIN_LENGTH })}
            error={error?.fields.password}
          />
          <Field
            label={t("auth.repeat_new_password")}
            name="confirm"
            type="password"
            autoComplete="new-password"
            required
            error={error?.fields.confirm}
          />
          <Button className="w-full" busy={busy}>
            {t("auth.change_password")}
          </Button>
        </form>
      )}
    </AuthShell>
  );
}

export function NotFoundPage() {
  const { t } = useT();
  usePageMeta("notFound");
  return (
    <AuthShell title={t("auth.not_found_title")} subtitle={t("auth.not_found_text")}>
      <Link to="/" className="text-sm font-medium text-brand-700 hover:underline">
        {t("auth.go_home")}
      </Link>
    </AuthShell>
  );
}
